
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { 
  ChevronRight, 
  Users, 
  Building2, 
  PlusSquare, 
  Calendar, 
  BarChart3,
  ArrowRight,
  LockKeyhole,
  Star,
  Award,
  Shield,
  Zap,
  CalendarCheck,
  Handshake,
  Briefcase,
  Airplay,
  PieChart,
  DollarSign,
  BookOpen
} from "lucide-react";

const Index = () => {
  const { user, loading } = useAuth();
  
  // If user is logged in, redirect to dashboard
  if (user && !loading) {
    return <Navigate to="/dashboard" />;
  }
  
  // Show loading indicator while auth is being checked
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
        <span className="ml-3">Loading...</span>
      </div>
    );
  }
  
  // Public landing page
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Go eXchange | Transform Relationship Capital into Business Value</title>
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-100 to-white py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                The eXchange: <span className="text-black">Grow & Monetize Your Network</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Join our curated, membership-based platform connecting vetted professionals with influential 
                connectors to accelerate business growth and revenue generation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-black hover:bg-gray-800 text-white">
                  Get Started <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
                <Button variant="outline" size="lg" className="border-gray-300 hover:bg-gray-100">
                  Book a Call <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              
              {/* Invitation-only banner */}
              <div className="mt-6 flex items-center p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <LockKeyhole className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0" />
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Membership is currently by invitation only.</span> Contact us to request access to our exclusive network.
                </p>
              </div>
              
              {/* Trusted by section */}
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="h-4 w-4 text-gray-500" />
                  <p className="text-sm font-medium text-gray-500">Trusted by industry leaders worldwide</p>
                </div>
                <div className="flex flex-wrap gap-6 items-center">
                  {/* Placeholder for client logos */}
                  <div className="h-8 w-24 bg-gray-200 rounded"></div>
                  <div className="h-8 w-24 bg-gray-200 rounded"></div>
                  <div className="h-8 w-24 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="bg-white p-8 rounded-lg shadow-xl border border-gray-200">
                <div className="aspect-video bg-gray-100 rounded-md mb-6 flex items-center justify-center">
                  <Award className="h-20 w-20 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Maximize Your Business Growth</h3>
                <p className="text-gray-600">
                  The eXchange connects vetted suppliers with influential connectors to drive new customer 
                  relationships and revenues through a trusted ecosystem powered by cutting-edge AI technology.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Platform Description */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Maximize Your Business Growth with The eXchange
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              The eXchange is a curated, membership-based platform that connects vetted suppliers with influential 
              connectors to drive new customer relationships and revenues. It emphasizes quality, trust, and mutual 
              benefit, aiming to accelerate entrepreneurial success by facilitating meaningful connections within 
              a trusted ecosystem powered by cutting-edge AI technology.
            </p>
            <Button size="lg" className="bg-black hover:bg-gray-800 text-white">
              Book a Discovery Call <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
      
      {/* Membership Model Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Membership Model</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join your eXchange Network through our comprehensive onboarding process
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center mb-4">1</div>
              <h3 className="text-xl font-bold mb-3">Initial Screening</h3>
              <p className="text-gray-600 mb-5">
                We review your background, network strength, and industry expertise to evaluate your potential contribution to our ecosystem.
              </p>
              <Button variant="outline" size="sm" className="border-gray-300 hover:bg-gray-100">
                Apply Now
              </Button>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center mb-4">2</div>
              <h3 className="text-xl font-bold mb-3">Welcome & Orientation</h3>
              <p className="text-gray-600 mb-5">
                Our platform introduction and setup of billing and commission structures, including integration with our AI-powered private platform.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center mb-4">3</div>
              <h3 className="text-xl font-bold mb-3">Network Integration</h3>
              <p className="text-gray-600 mb-5">
                Deep profile creation with AI assistance, matching with complementary Exchange Members, and setup of your first monthly meeting.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center mb-4">4</div>
              <h3 className="text-xl font-bold mb-3">Training & Engagement</h3>
              <p className="text-gray-600 mb-5">
                Complete our platform training on the CRM system, lead management, and commission tracking to begin active lead sharing.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Exchange Roles Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Exchange Roles</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Specialized positions for maximum efficiency. Our platform features distinct roles to create a self-sustaining ecosystem 
              where everyone has clear incentives aligned with group success.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Handshake className="h-10 w-10 text-black mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">Connectors</h3>
              <p className="text-gray-600">
                Network facilitators with extensive relationship capital who share valuable introductions and earn commissions from successful connections.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Briefcase className="h-10 w-10 text-black mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">Suppliers</h3>
              <p className="text-gray-600">
                Providers of specific products or services who gain access to qualified leads with reduced customer acquisition costs through clear commission structures.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Airplay className="h-10 w-10 text-black mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">Air Traffic Controllers (ATCs)</h3>
              <p className="text-gray-600">
                Dedicated facilitators who coordinate between members, track lead flow, handle administrative tasks, and ensure smooth operation of your exchange.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">What Our Members Say</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-600 italic mb-4">
                "The eXchange revolutionized how we monetize our network."
              </p>
              <p className="font-semibold">Connector Testimonial, Position</p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-600 italic mb-4">
                "The qualified leads we receive have transformed our sales pipeline."
              </p>
              <p className="font-semibold">Supplier Testimonial, Position</p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-600 italic mb-4">
                "Our Air Traffic Controller makes connections seamless."
              </p>
              <p className="font-semibold">Member Testimonial, Position</p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-600 italic mb-4">
                "The commission structure creates perfect incentive alignment."
              </p>
              <p className="font-semibold">Testimonial Author, Position</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Monthly Power Hour */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <CalendarCheck className="h-12 w-12 mb-6" />
              <h2 className="text-3xl font-bold mb-4">Monthly Power Hour</h2>
              <h3 className="text-xl mb-6">Experience The eXchange in Action</h3>
              <p className="text-gray-300 mb-6">
                Our streamlined 60-minute online meeting format maximizes efficiency and engagement through:
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0" />
                  <span>Rapid-fire updates (2 minutes per member)</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0" />
                  <span>Opportunity deep dives on pre-selected deals</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0" />
                  <span>Strategic matching for specific introduction needs</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0" />
                  <span>AI-assisted connection recommendations</span>
                </li>
              </ul>
              <Button variant="outline" className="border-white hover:bg-white/10 text-white">
                See Meeting Schedule
              </Button>
            </div>
            <div className="md:w-1/2">
              <div className="bg-gray-800 rounded-lg p-6 aspect-video flex items-center justify-center">
                <Calendar className="h-24 w-24 text-gray-600" />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Why We're Different */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why We're Different</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We exist to become the premier go-to resource for entrepreneurs seeking what they need to scale.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <Shield className="h-10 w-10 text-black mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">Trust & Integrity</h3>
              <p className="text-gray-600">
                We prioritize integrity in all interactions and rigorously vet all participants to maintain the highest standards.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <DollarSign className="h-10 w-10 text-black mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">Revenue Generation</h3>
              <p className="text-gray-600">
                Our standard commission structure ensures transparent tracking of all deals from introduction to closure.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <Zap className="h-10 w-10 text-black mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">AI-Powered Networking</h3>
              <p className="text-gray-600">
                Our platform leverages advanced AI technology to identify optimal connections and maximize success rates.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Platform Features */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Platform Features</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Technology-driven lead exchange. The eXchange combines human expertise with cutting-edge technology.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Zap className="h-10 w-10 text-black mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">AI-Powered Matching Algorithm</h3>
              <p className="text-gray-600">
                Our advanced AI analyzes member profiles and network connections to suggest optimal introductions.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Shield className="h-10 w-10 text-black mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">Secure Transaction Management</h3>
              <p className="text-gray-600">
                Our private platform ensures all interactions are tracked and protected.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Users className="h-10 w-10 text-black mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">Shared CRM System</h3>
              <p className="text-gray-600">
                Comprehensive tracking of all leads, deals, and commissions in one secure location.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <PieChart className="h-10 w-10 text-black mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">Performance Analytics</h3>
              <p className="text-gray-600">
                Real-time metrics on lead quality, conversion rates, and revenue generation.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Revenue Sharing Model */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Revenue Sharing Model</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <ul className="space-y-4 text-lg">
                <li className="flex items-center gap-3">
                  <DollarSign className="h-6 w-6 text-gray-900" />
                  <span>Connectors: 20% of collected revenue for introductions</span>
                </li>
                <li className="flex items-center gap-3">
                  <DollarSign className="h-6 w-6 text-gray-900" />
                  <span>ATCs: 40% for full sales cycle support</span>
                </li>
                <li className="flex items-center gap-3">
                  <DollarSign className="h-6 w-6 text-gray-900" />
                  <span>Suppliers: Remaining percentage after commissions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Vision */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Vision</h2>
            <p className="text-lg text-gray-600 mb-8">
              The eXchange aims to become the premier go-to resource where entrepreneurs find what they need 
              to scale their businesses efficiently and effectively. We believe in fostering a global community 
              of mutual growth and success through technology-enhanced human connections.
            </p>
            <Button variant="outline" size="lg" className="border-gray-300 hover:bg-gray-100">
              <BookOpen className="mr-2 h-4 w-4" /> Read Our Full Vision
            </Button>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Start Your Journey Today</h2>
          <p className="text-lg text-gray-300 mb-3 max-w-2xl mx-auto">
            Your membership begins with $1,000 monthly
          </p>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            (offset by revenues earned)
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white hover:bg-gray-100 text-black">
              <Link to="/auth">Book Discovery Call</Link>
            </Button>
            <Button variant="outline" size="lg" className="border-white hover:bg-white/10 text-white">
              <Link to="/auth">Request Invitation</Link>
            </Button>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Go eXchange</h3>
              <p className="mb-4">
                The premier ecosystem where relationship capital is transformed into measurable business value.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/auth" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/auth" className="hover:text-white transition-colors">How It Works</Link></li>
                <li><Link to="/auth" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/auth" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Twitter</a></li>
                <li><a href="#" className="hover:text-white transition-colors">LinkedIn</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Instagram</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-sm">
            <p>© {new Date().getFullYear()} Go eXchange. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
